type LangCode = 'en' | 'es' | 'pt' | 'fr' | 'ar' | 'ja' | 'de';

type I18nDict = {
  date: string;
  heroTitle1: string;
  heroTitle2: string;
  heroBody: string;
  search: string;
  group: string;
  empty: string;
  knockoutNote: string;
  ctaEmpty: string;
  ctaOne: string;
  ctaMany: string;
  ctaAction: string;
  sheetTitle: string;
  subscribeSubhead: string;
  langSheetTitle: string;
  langSheetSub: string;
  language: string;
  btnGoogle: string;
  btnApple: string;
  btnOutlook: string;
  btnCopy: string;
  subGoogle: string;
  subApple: string;
  subOutlook: string;
  subCopy: string;
  copyLabel: string;
  copied: string;
  confirmBadge: string;
  confirmCta: string;
  refine: string;
  giveFeedback: string;
  selectAll: string;
  clearAll: string;
  ariaSelectAll: string;
  ariaClearAll: string;
  roundOf32: string;
  roundOf16: string;
  quarterFinal: string;
  semiFinal: string;
  thirdPlace: string;
  finalRound: string;
};

const DICT: Record<LangCode, I18nDict> = {
  en: {
    date: 'JUN 11 → JUL 19',
    heroTitle1: 'Your World Cup,',
    heroTitle2: 'in your calendar.',
    heroBody: "Pick the teams you follow. We'll add every match to your calendar — and update it automatically as knockouts are confirmed.",
    search: 'Search 32 teams',
    group: 'Group',
    empty: 'No teams match',
    knockoutNote: 'Knockout matches are added to your calendar as FIFA confirms each bracket — no need to come back.',
    ctaEmpty: 'Pick at least one team',
    ctaOne: '1 team selected',
    ctaMany: '{n} teams selected',
    ctaAction: 'Add to Calendar',
    sheetTitle: 'Add to calendar',
    subscribeSubhead: '{n} teams · {m} matches will appear automatically.',
    langSheetTitle: 'Language',
    langSheetSub: 'Pick a display language. Your selection saves automatically.',
    language: 'Language',
    btnGoogle: 'Google Calendar',
    btnApple: 'Apple Calendar',
    btnOutlook: 'Outlook',
    btnCopy: 'Copy iCal URL',
    subGoogle: 'Opens in browser',
    subApple: 'iCloud subscription',
    subOutlook: 'Webcal subscription',
    subCopy: 'For any other app',
    copyLabel: 'Copy this URL to subscribe:',
    copied: 'Copied!',
    confirmBadge: '{n} teams added to your calendar',
    confirmCta: '☕ Buy me a coffee',
    refine: 'Refine selection',
    giveFeedback: 'Give feedback',
    selectAll: 'Select all',
    clearAll: 'Clear',
    ariaSelectAll: 'Select all 32 teams',
    ariaClearAll: 'Clear all selections',
    roundOf32: 'Round of 32',
    roundOf16: 'Round of 16',
    quarterFinal: 'Quarter-final',
    semiFinal: 'Semi-final',
    thirdPlace: 'Third-place play-off',
    finalRound: 'Final',
  },
  es: {
    date: '11 JUN → 19 JUL',
    heroTitle1: 'Tu Mundial,',
    heroTitle2: 'en tu calendario.',
    heroBody: 'Elige las selecciones que sigues. Añadiremos cada partido a tu calendario y lo actualizaremos automáticamente al confirmarse las eliminatorias.',
    search: 'Buscar entre 32 selecciones',
    group: 'Grupo',
    empty: 'No se encontraron selecciones',
    knockoutNote: 'Los partidos de las eliminatorias se añaden a tu calendario en cuanto la FIFA confirma cada cruce — sin tener que volver.',
    ctaEmpty: 'Selecciona al menos una selección',
    ctaOne: '1 selección elegida',
    ctaMany: '{n} selecciones elegidas',
    ctaAction: 'Añadir al calendario',
    sheetTitle: 'Añadir al calendario',
    subscribeSubhead: '{n} selecciones · {m} partidos aparecerán automáticamente.',
    langSheetTitle: 'Idioma',
    langSheetSub: 'Elige un idioma de visualización. Tu selección se guarda automáticamente.',
    language: 'Idioma',
    btnGoogle: 'Google Calendar',
    btnApple: 'Apple Calendar',
    btnOutlook: 'Outlook',
    btnCopy: 'Copiar URL iCal',
    subGoogle: 'Abrir en el navegador',
    subApple: 'Suscripción iCloud',
    subOutlook: 'Suscripción Webcal',
    subCopy: 'Para cualquier otra app',
    copyLabel: 'Copia esta URL para suscribirte:',
    copied: '¡Copiado!',
    confirmBadge: '{n} selecciones añadidas a tu calendario',
    confirmCta: '☕ Invítame a un café',
    refine: 'Refinar selección',
    giveFeedback: 'Dar opinión',
    selectAll: 'Todos',
    clearAll: 'Limpiar',
    ariaSelectAll: 'Seleccionar las 32 selecciones',
    ariaClearAll: 'Borrar todas las selecciones',
    roundOf32: 'Octavos de final',
    roundOf16: 'Octavos de final',
    quarterFinal: 'Cuartos de final',
    semiFinal: 'Semifinal',
    thirdPlace: 'Tercer puesto',
    finalRound: 'Final',
  },
  pt: {
    date: '11 JUN → 19 JUL',
    heroTitle1: 'A Copa do Mundo,',
    heroTitle2: 'no seu calendário.',
    heroBody: 'Escolha as seleções que você acompanha. Vamos adicionar cada jogo ao seu calendário — e atualizar automaticamente conforme o mata-mata for confirmado.',
    search: 'Buscar entre 32 seleções',
    group: 'Grupo',
    empty: 'Nenhuma seleção encontrada',
    knockoutNote: 'Os jogos do mata-mata entram no seu calendário assim que a FIFA confirma cada chave — sem precisar voltar aqui.',
    ctaEmpty: 'Escolha ao menos uma seleção',
    ctaOne: '1 seleção escolhida',
    ctaMany: '{n} seleções escolhidas',
    ctaAction: 'Adicionar ao calendário',
    sheetTitle: 'Adicionar ao calendário',
    subscribeSubhead: '{n} seleções · {m} jogos vão aparecer automaticamente.',
    langSheetTitle: 'Idioma',
    langSheetSub: 'Escolha um idioma de exibição. Sua escolha é salva automaticamente.',
    language: 'Idioma',
    btnGoogle: 'Google Calendar',
    btnApple: 'Apple Calendar',
    btnOutlook: 'Outlook',
    btnCopy: 'Copiar URL iCal',
    subGoogle: 'Abrir no navegador',
    subApple: 'Assinatura iCloud',
    subOutlook: 'Assinatura Webcal',
    subCopy: 'Para qualquer outro app',
    copyLabel: 'Copie esta URL para assinar:',
    copied: 'Copiado!',
    confirmBadge: '{n} seleções adicionadas ao seu calendário',
    confirmCta: '☕ Me pague um café',
    refine: 'Ajustar seleção',
    giveFeedback: 'Dar feedback',
    selectAll: 'Todas',
    clearAll: 'Limpar',
    ariaSelectAll: 'Selecionar as 32 seleções',
    ariaClearAll: 'Limpar todas as seleções',
    roundOf32: 'Rodada de 32',
    roundOf16: 'Oitavas de final',
    quarterFinal: 'Quartas de final',
    semiFinal: 'Semifinal',
    thirdPlace: 'Terceiro lugar',
    finalRound: 'Final',
  },
  fr: {
    date: '11 JUIN → 19 JUIL',
    heroTitle1: 'Votre Coupe du Monde,',
    heroTitle2: 'dans votre calendrier.',
    heroBody: "Choisissez les sélections que vous suivez. Nous ajouterons chaque match à votre calendrier — et le mettrons à jour automatiquement dès que les phases finales seront confirmées.",
    search: 'Rechercher parmi 32 sélections',
    group: 'Groupe',
    empty: 'Aucune sélection trouvée',
    knockoutNote: "Les matchs à élimination directe sont ajoutés à votre calendrier dès que la FIFA confirme chaque tableau — pas besoin de revenir.",
    ctaEmpty: 'Choisissez au moins une sélection',
    ctaOne: '1 sélection choisie',
    ctaMany: '{n} sélections choisies',
    ctaAction: 'Ajouter au calendrier',
    sheetTitle: 'Ajouter au calendrier',
    subscribeSubhead: '{n} sélections · {m} matchs apparaîtront automatiquement.',
    langSheetTitle: 'Langue',
    langSheetSub: "Choisissez une langue d'affichage. Votre choix est enregistré automatiquement.",
    language: 'Langue',
    btnGoogle: 'Google Calendar',
    btnApple: 'Apple Calendar',
    btnOutlook: 'Outlook',
    btnCopy: "Copier l'URL iCal",
    subGoogle: 'Ouvrir dans le navigateur',
    subApple: 'Abonnement iCloud',
    subOutlook: 'Abonnement Webcal',
    subCopy: 'Pour toute autre application',
    copyLabel: 'Copiez cette URL pour vous abonner :',
    copied: 'Copié !',
    confirmBadge: '{n} sélections ajoutées à votre calendrier',
    confirmCta: '☕ Offrez-moi un café',
    refine: 'Modifier la sélection',
    giveFeedback: 'Donner un avis',
    selectAll: 'Tout',
    clearAll: 'Effacer',
    ariaSelectAll: 'Sélectionner les 32 sélections',
    ariaClearAll: 'Effacer toutes les sélections',
    roundOf32: 'Tour de 32',
    roundOf16: 'Huitièmes de finale',
    quarterFinal: 'Quart de finale',
    semiFinal: 'Demi-finale',
    thirdPlace: 'Match pour la 3e place',
    finalRound: 'Finale',
  },
  ar: {
    date: '١١ يونيو ← ١٩ يوليو',
    heroTitle1: 'كأس العالم،',
    heroTitle2: 'في تقويمك.',
    heroBody: 'اختر المنتخبات التي تتابعها وسنضيف كل المباريات إلى تقويمك — ونحدّثها تلقائياً بمجرد تأكيد الأدوار الإقصائية.',
    search: 'ابحث بين ٣٢ منتخباً',
    group: 'المجموعة',
    empty: 'لا توجد نتائج تطابق بحثك',
    knockoutNote: 'تُضاف مباريات الأدوار الإقصائية (ابتداءً من دور الـ ١٦) إلى تقويمك فور تأكيد فيفا لكل جدول — دون الحاجة للعودة.',
    ctaEmpty: 'اختر منتخباً واحداً على الأقل',
    ctaOne: 'منتخب واحد مختار',
    ctaMany: '{n} منتخبات مختارة',
    ctaAction: 'أضف إلى التقويم',
    sheetTitle: 'أضف إلى التقويم',
    subscribeSubhead: '{n} منتخبات · {m} مباريات ستظهر تلقائياً.',
    langSheetTitle: 'اللغة',
    langSheetSub: 'اختر لغة العرض. يتم حفظ اختيارك تلقائياً.',
    language: 'اللغة',
    btnGoogle: 'تقويم جوجل',
    btnApple: 'تقويم آبل',
    btnOutlook: 'أوتلوك',
    btnCopy: 'نسخ رابط iCal',
    subGoogle: 'يفتح في المتصفح',
    subApple: 'اشتراك آي كلاود',
    subOutlook: 'اشتراك Webcal',
    subCopy: 'لأي تطبيق آخر',
    copyLabel: 'انسخ هذا الرابط للاشتراك:',
    copied: 'تم النسخ!',
    confirmBadge: 'تمت إضافة {n} منتخبات إلى تقويمك',
    confirmCta: '☕ ادعمني بكوب قهوة',
    refine: 'تعديل الاختيارات',
    giveFeedback: 'شارك رأيك',
    selectAll: 'تحديد الكل',
    clearAll: 'مسح',
    ariaSelectAll: 'تحديد كل المنتخبات الـ ٤٨',
    ariaClearAll: 'مسح كل الاختيارات',
    roundOf32: 'دور الـ ٣٢',
    roundOf16: 'دور الـ ١٦',
    quarterFinal: 'ربع النهائي',
    semiFinal: 'نصف النهائي',
    thirdPlace: 'مباراة المركز الثالث',
    finalRound: 'النهائي',
  },
  ja: {
    date: '6月11日 → 7月19日',
    heroTitle1: 'あなたのワールドカップを、',
    heroTitle2: 'カレンダーに。',
    heroBody: '応援する代表チームを選んでください。全試合をカレンダーに追加し、決勝トーナメントが決まり次第、自動でアップデートします。',
    search: '48の代表チームから検索',
    group: 'グループ',
    empty: '一致するチームはありません',
    knockoutNote: '決勝トーナメントの試合は、FIFAが対戦カードを確定し次第、自動でカレンダーに追加されます。',
    ctaEmpty: 'チームを1つ以上選んでください',
    ctaOne: '1チーム選択中',
    ctaMany: '{n}チーム選択中',
    ctaAction: 'カレンダーに追加',
    sheetTitle: 'カレンダーに追加',
    subscribeSubhead: '{n}チーム · {m}試合が自動的に表示されます。',
    langSheetTitle: '言語',
    langSheetSub: '表示言語を選択してください。選択は自動で保存されます。',
    language: '言語',
    btnGoogle: 'Google カレンダー',
    btnApple: 'Apple カレンダー',
    btnOutlook: 'Outlook',
    btnCopy: 'iCal URLをコピー',
    subGoogle: 'ブラウザで開く',
    subApple: 'iCloud サブスクリプション',
    subOutlook: 'Webcal サブスクリプション',
    subCopy: 'その他のアプリ用',
    copyLabel: '登録するURLをコピー:',
    copied: 'コピーしました！',
    confirmBadge: '{n}チームをカレンダーに追加しました',
    confirmCta: '☕ コーヒーを奢る',
    refine: '選択内容を変更',
    giveFeedback: 'フィードバックを送る',
    selectAll: 'すべて選択',
    clearAll: 'クリア',
    ariaSelectAll: '48チームすべて選択',
    ariaClearAll: 'すべての選択を解除',
    roundOf32: 'ラウンド・オブ32',
    roundOf16: 'ラウンド・オブ16',
    quarterFinal: '準々決勝',
    semiFinal: '準決勝',
    thirdPlace: '3位決定戦',
    finalRound: '決勝',
  },
  de: {
    date: '11. JUN → 19. JUL',
    heroTitle1: 'Deine WM,',
    heroTitle2: 'in deinem Kalender.',
    heroBody: 'Wähle die Mannschaften, denen du folgst. Wir fügen alle Spiele zu deinem Kalender hinzu — und aktualisieren ihn automatisch, wenn die K.O.-Runde feststeht.',
    search: '48 Mannschaften durchsuchen',
    group: 'Gruppe',
    empty: 'Keine Mannschaft gefunden',
    knockoutNote: 'K.O.-Spiele werden automatisch zu deinem Kalender hinzugefügt, sobald die FIFA jeden Spielplan bestätigt — ohne dass du zurückkehren musst.',
    ctaEmpty: 'Mindestens eine Mannschaft auswählen',
    ctaOne: '1 Mannschaft ausgewählt',
    ctaMany: '{n} Mannschaften ausgewählt',
    ctaAction: 'Zum Kalender hinzufügen',
    sheetTitle: 'Zum Kalender hinzufügen',
    subscribeSubhead: '{n} Mannschaften · {m} Spiele erscheinen automatisch.',
    langSheetTitle: 'Sprache',
    langSheetSub: 'Wähle eine Anzeigesprache. Deine Auswahl wird automatisch gespeichert.',
    language: 'Sprache',
    btnGoogle: 'Google Kalender',
    btnApple: 'Apple Kalender',
    btnOutlook: 'Outlook',
    btnCopy: 'iCal-URL kopieren',
    subGoogle: 'Öffnet im Browser',
    subApple: 'iCloud-Abonnement',
    subOutlook: 'Webcal-Abonnement',
    subCopy: 'Für jede andere App',
    copyLabel: 'Kopiere diese URL zum Abonnieren:',
    copied: 'Kopiert!',
    confirmBadge: '{n} Mannschaften zu deinem Kalender hinzugefügt',
    confirmCta: '☕ Kauf mir einen Kaffee',
    refine: 'Auswahl anpassen',
    giveFeedback: 'Feedback geben',
    selectAll: 'Alle',
    clearAll: 'Löschen',
    ariaSelectAll: 'Alle 48 Mannschaften auswählen',
    ariaClearAll: 'Alle Auswahlen löschen',
    roundOf32: 'Runde der 32',
    roundOf16: 'Runde der 16',
    quarterFinal: 'Viertelfinale',
    semiFinal: 'Halbfinale',
    thirdPlace: 'Spiel um Platz 3',
    finalRound: 'Finale',
  },
};

export const LANGUAGES: { code: LangCode; native: string; english: string; rtl?: boolean }[] = [
  { code: 'en', native: 'English',   english: 'English' },
  { code: 'es', native: 'Español',   english: 'Spanish' },
  { code: 'pt', native: 'Português', english: 'Portuguese' },
  { code: 'fr', native: 'Français',  english: 'French' },
  { code: 'ar', native: 'العربية',    english: 'Arabic', rtl: true },
  { code: 'ja', native: '日本語',     english: 'Japanese' },
  { code: 'de', native: 'Deutsch',    english: 'German' },
];

export type { LangCode };

const STORAGE_KEY = 'wcc-lang';

export function getLang(): LangCode {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored && stored in DICT) return stored as LangCode;
  const browser = navigator.language.slice(0, 2) as LangCode;
  return browser in DICT ? browser : 'en';
}

export function t(key: keyof I18nDict): string {
  const lang = getLang();
  return (DICT[lang]?.[key] ?? DICT.en[key]) as string;
}

export function fill(template: string, vars: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (_, k) => String(vars[k] ?? `{${k}}`));
}

export function setLang(code: LangCode): void {
  const from = getLang();
  localStorage.setItem(STORAGE_KEY, code);
  if (from !== code) {
    import('./analytics.js').then(({ track }) => track('language_changed', { from_lang: from, to_lang: code }));
  }
  const meta = LANGUAGES.find(l => l.code === code);
  const html = document.documentElement;
  html.lang = code;
  html.dir = meta?.rtl ? 'rtl' : 'ltr';
  applyTranslations();
  const codeEl = document.getElementById('lang-code');
  if (codeEl) codeEl.textContent = code.toUpperCase();
}

export function applyTranslations(): void {
  document.querySelectorAll<HTMLElement>('[data-i18n]').forEach(el => {
    const key = el.dataset.i18n as keyof I18nDict;
    if (key) {
      // If element has no children, just set text
      if (el.children.length === 0) {
        el.textContent = t(key);
      } else {
        // Find the first text node or a designated container
        // This is a simple implementation; for complex structures, 
        // we might need more specific selectors.
        const textNode = Array.from(el.childNodes).find(node => node.nodeType === Node.TEXT_NODE);
        if (textNode) {
          textNode.textContent = t(key);
        }
      }
    }
  });
  const search = document.getElementById('team-search') as HTMLInputElement | null;
  if (search) search.placeholder = t('search');
  const dateEl = document.getElementById('header-dates');
  if (dateEl) dateEl.textContent = t('date');
  const selectAllBtn = document.getElementById('select-all-btn');
  if (selectAllBtn) {
    const key = selectAllBtn.dataset.i18n;
    if (key === 'selectAll') selectAllBtn.setAttribute('aria-label', t('ariaSelectAll'));
    if (key === 'clearAll') selectAllBtn.setAttribute('aria-label', t('ariaClearAll'));
  }
  const lang = getLang();
  const langKey = `name${lang.charAt(0).toUpperCase()}${lang.slice(1)}` as keyof DOMStringMap;
  document.querySelectorAll<HTMLElement>('.team-tile-name').forEach(el => {
    const localized = el.dataset[langKey];
    if (localized) el.textContent = localized;
  });
}

export function renderLangList(container: HTMLElement, onSelect: (code: LangCode) => void): void {
  const current = getLang();
  container.innerHTML = '';
  for (const lang of LANGUAGES) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'lang-option' + (lang.code === current ? ' lang-option--active' : '');
    btn.dataset.code = lang.code;
    const native = document.createElement('span');
    native.className = 'lang-option-native';
    native.textContent = lang.native;
    const english = document.createElement('span');
    english.className = 'lang-option-english';
    english.textContent = lang.english;
    btn.appendChild(native);
    btn.appendChild(english);
    btn.addEventListener('click', () => onSelect(lang.code));
    container.appendChild(btn);
  }
}
